import { useState } from "react";
import { useNavigate } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useOptionalCustomerBasketAdapter } from "@shared-core/data/CustomerBasketAdapterContext";
import { customerBasketFailure } from "@shared-core/domain/customer-basket-presentation";
import { Button } from "../../../app/components/primitives/button";
import { useCustomerBasket } from "./CustomerBasketProvider";
import { cn } from "../../../app/components/primitives/utils";
import { Loader2, RotateCcw } from "lucide-react";

/** Whether this build offers "Order again" at all: a basket the person can reorder into. A list uses it to decide
 *  whether to leave room for the button, the button itself to decide whether to draw. */
export function useOrderAgainOffered(): boolean {
  const baskets = useOptionalCustomerBasketAdapter();
  const basket = useCustomerBasket();
  return Boolean(baskets && basket && basket.capabilities.reorder);
}

// "Order again" on a past order (docs/features/customer-portal-ordering/17 section 3.6): the server rebuilds
// the order's lines in the person's basket, priced today, and the person lands on the basket, where any line
// that can no longer be ordered says why. Only a row the server gave a `reorder_url` shows it (never a
// disabled button that says nothing), and nothing shows in a build with no basket or whose reorder route does not exist yet.
export function OrderAgainButton({
  reorderUrl,
  orderLabel,
  compact = false,
}: {
  reorderUrl: string | null | undefined;
  /** The order's title, so the button says which order it repeats. */
  orderLabel: string;
  /** For a dense list row: the label shows only from a `3xl` wide container up (an icon button below it; the list is the container), and a refusal opens
   *  beside the button rather than growing the row. */
  compact?: boolean;
}) {
  const baskets = useOptionalCustomerBasketAdapter();
  const basket = useCustomerBasket();
  const offered = useOrderAgainOffered();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!reorderUrl || !baskets || !basket || !offered) return null;

  async function orderAgain(url: string) {
    if (!baskets) return;
    setBusy(true);
    setError(null);
    try {
      await baskets.reorderOrder(url);
      basket?.refresh();
      navigate(paths.portalBasket());
    } catch (err) {
      setError(customerBasketFailure(err).summary);
      setBusy(false);
    }
  }

  return (
    <div className={compact ? "relative" : "space-y-1"}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(compact && "@max-3xl:size-8 @max-3xl:px-0")}
        disabled={busy}
        aria-label={`Order again: ${orderLabel}`}
        onClick={() => void orderAgain(reorderUrl)}
      >
        {busy ? <Loader2 className="animate-spin" /> : <RotateCcw />}
        <span className={cn(compact && "@max-3xl:sr-only")}>Order again</span>
      </Button>
      {error && (
        <p
          role="alert"
          className={cn(
            "text-xs text-red-700 dark:text-red-400",
            compact &&
              "absolute right-0 top-full z-20 mt-1 w-64 rounded-md border bg-background p-2 text-left shadow-md",
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
}
